import { useQueries } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  errorMessage,
  guildsApi,
  qk,
  useBulkMappings,
  useCreateJob,
  useGuilds,
  useTargets,
  type ChannelNode,
  type ChannelQuery,
  type ChannelType,
  type Guild,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useDebounced } from "@/components/useDebounced";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import {
  buildGuildTree,
  isMappable,
  statusQuery,
  type BuiltNode,
  type StatusFilter,
} from "@/lib/channelTree";
import { plural } from "@/lib/format";
import {
  Badge,
  BulkBar,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  Skeleton,
  Tree,
  useToast,
  type TreeNode,
} from "@/ui";
import "./pages.css";

const TYPE_OPTIONS: Array<{ value: "" | ChannelType; label: string }> = [
  { value: "", label: "All types" },
  { value: "text", label: "Text" },
  { value: "news", label: "Announcement" },
  { value: "forum", label: "Forum" },
  { value: "thread", label: "Thread" },
  { value: "voice", label: "Voice" },
];
const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All channels" },
  { value: "mapped", label: "Mapped" },
  { value: "unmapped", label: "Unmapped" },
  { value: "inaccessible", label: "No access" },
];

export function SourcesPage() {
  useDocumentTitle("Sources");
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 250);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [type, setType] = useState<"" | ChannelType>("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [mapOpen, setMapOpen] = useState(false);

  const guilds = useGuilds();
  const targets = useTargets();
  const toast = useToast();
  const createJob = useCreateJob();
  const bulk = useBulkMappings();

  const filtering = q !== "" || status !== "all" || type !== "";
  const channelQuery: ChannelQuery = useMemo(
    () => ({ q: q || undefined, type: type || undefined, ...statusQuery(status) }),
    [q, status, type],
  );
  const guildList = guilds.data?.items ?? [];
  // Load channels for expanded guilds, or for every guild while a filter is active.
  const loadFor = guildList.filter((g) => filtering || expanded.has(`guild:${g.id}`));
  const channelResults = useQueries({
    queries: loadFor.map((g) => ({
      queryKey: qk.channels(g.id, channelQuery),
      queryFn: () => guildsApi.channels(g.id, channelQuery),
      placeholderData: (prev: unknown) => prev as never,
    })),
  });

  const channelsByGuild = new Map<string, ChannelNode[]>();
  loadFor.forEach((g, i) => {
    const data = channelResults[i]?.data;
    if (data) channelsByGuild.set(g.id, data.items);
  });
  const channelError = channelResults.find((r) => r.error)?.error;
  const loadingChannels = channelResults.some((r) => r.isLoading);

  const targetName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of targets.data?.items ?? []) m.set(t.id, t.name);
    return m;
  }, [targets.data]);

  // id → channel for every loaded channel (selection is resolved against this).
  const channelIndex = new Map<string, ChannelNode>();
  for (const list of channelsByGuild.values()) for (const c of list) channelIndex.set(c.id, c);

  const visibleGuilds = filtering
    ? guildList.filter((g) => (channelsByGuild.get(g.id)?.length ?? 1) > 0)
    : guildList;

  const nodes: TreeNode[] = visibleGuilds.map((g) => {
    const list = channelsByGuild.get(g.id);
    const built = buildGuildTree(g, list ?? []);
    const [node] = toTreeNodes(built, targetName);
    // Unloaded guilds get a placeholder child so they can be expanded (triggers the fetch).
    if (!list) node.children = [{ id: `loading:${g.id}`, label: "Loading…", kind: "channel", disabled: true }];
    return node;
  });

  const expandedIds: ReadonlySet<string> = filtering
    ? new Set([...expanded, ...visibleGuilds.map((g) => `guild:${g.id}`)])
    : expanded;

  // Only real, readable, mappable channels count as a selection.
  const selectedChannels = [...selected]
    .map((id) => channelIndex.get(id))
    .filter((c): c is ChannelNode => !!c && c.type !== "category");
  const mappable = selectedChannels.filter(isMappable);
  const mappingIds = selectedChannels.flatMap((c) => c.mappings.map((m) => m.id));
  const unmapped = mappable.filter((c) => c.mappings.length === 0);

  const clear = () => setSelected(new Set());

  const startJob = async (
    kind: "bulk_create" | "bulk_map",
    ids: string[],
    extra: Record<string, unknown> = {},
  ) => {
    try {
      await createJob.mutateAsync({
        type: kind,
        payload: { source_channel_ids: ids, ...extra },
      });
      toast.show({
        tone: "success",
        title: kind === "bulk_create" ? `Creating ${plural(ids.length, "channel")}` : `Mapping ${plural(ids.length, "channel")}`,
        message: "Job started. Progress is on the Jobs page.",
      });
      clear();
    } catch (e) {
      toast.show({ tone: "danger", title: "Could not start job", message: errorMessage(e) });
    }
  };

  const setEnabled = async (enabled: boolean) => {
    try {
      const res = await bulk.mutateAsync({ ids: mappingIds, action: enabled ? "enable" : "disable" });
      toast.show({
        tone: "success",
        title: `${enabled ? "Enabled" : "Disabled"} ${plural(res.updated, "mapping")}`,
      });
    } catch (e) {
      toast.show({ tone: "danger", title: "Bulk update failed", message: errorMessage(e) });
    }
  };

  const busy = createJob.isPending || bulk.isPending;

  return (
    <div className="page">
      <PageHeader
        title="Sources"
        description="Channels the listener account can see. Select channels to map them, create matching channels, or pause them."
      />
      <div className="filters" role="search">
        <Input
          className="filters__grow"
          label="Search channels"
          placeholder="Channel name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          type="search"
        />
        <Select
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        />
        <Select
          label="Type"
          value={type}
          options={TYPE_OPTIONS}
          onChange={(e) => setType(e.target.value as "" | ChannelType)}
        />
      </div>
      <ErrorBanner error={guilds.error ?? channelError} onRetry={() => guilds.refetch()} />

      <div className="split">
        <Card padding="sm">
          {guilds.isLoading ? (
            <Skeleton lines={8} />
          ) : visibleGuilds.length === 0 && !loadingChannels ? (
            <EmptyState
              title={filtering ? "No channels match" : "No source servers yet"}
              description={
                filtering
                  ? "Try a different name or clear the filters."
                  : "Add an account token; the listener lists the servers it can read."
              }
              action={
                filtering ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSearch("");
                      setStatus("all");
                      setType("");
                    }}
                  >
                    Clear filters
                  </Button>
                ) : (
                  <Link className="link" to="/accounts">
                    Open Account & token
                  </Link>
                )
              }
            />
          ) : (
            <div className="sources-tree">
              <Tree
                label="Source servers and channels"
                nodes={nodes}
                selected={selected}
                onSelectionChange={setSelected}
                expanded={expandedIds}
                onExpandedChange={setExpanded}
              />
            </div>
          )}
          <BulkBar
            count={selectedChannels.length}
            noun={{ one: "channel", other: "channels" }}
            onClear={clear}
            busy={busy}
            actions={
              <>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || unmapped.length === 0}
                  onClick={() => startJob("bulk_create", unmapped.map((c) => c.id), { map: true })}
                >
                  Create {plural(unmapped.length, "channel")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy || mappable.length === 0}
                  onClick={() => setMapOpen(true)}
                >
                  Map {mappable.length} to channel…
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || mappingIds.length === 0}
                  onClick={() => setEnabled(true)}
                >
                  Enable
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || mappingIds.length === 0}
                  onClick={() => setEnabled(false)}
                >
                  Disable
                </Button>
              </>
            }
          />
        </Card>

        <SelectionPanel
          guilds={visibleGuilds}
          selected={selectedChannels}
          mappable={mappable.length}
          unmapped={unmapped.length}
          mappings={mappingIds.length}
        />
      </div>

      <MapToTargetModal
        open={mapOpen}
        count={mappable.length}
        onClose={() => setMapOpen(false)}
        busy={createJob.isPending}
        onConfirm={async (targetId) => {
          await startJob(
            "bulk_map",
            mappable.map((c) => c.id),
            { target_channel_id: targetId },
          );
          setMapOpen(false);
        }}
      />
    </div>
  );
}

/**
 * BuiltNode → Tree node(s). Threads are emitted as siblings right after their parent
 * channel (Tree only lets leaves be selected, so a channel must stay a leaf).
 */
function toTreeNodes(n: BuiltNode, targetName: Map<string, string>): TreeNode[] {
  const kids = (list: BuiltNode[]) => list.flatMap((c) => toTreeNodes(c, targetName));
  if (n.kind === "guild") {
    const g = n.guild!;
    return [
      {
        id: n.id,
        label: g.name,
        kind: "guild",
        disabled: !g.accessible,
        meta: (
          <span className="faint">
            {g.mapped_count}/{g.channel_count} mapped
          </span>
        ),
        children: kids(n.children),
      },
    ];
  }
  const c = n.channel!;
  if (n.kind === "category") {
    return [
      {
        id: n.id,
        label: c.name,
        kind: "category",
        disabled: !c.accessible || n.children.length === 0,
        meta: c.accessible ? undefined : <Badge tone="warning">no access</Badge>,
        children: kids(n.children),
      },
    ];
  }
  const targets = c.mappings.map((m) => targetName.get(m.target_channel_id) ?? m.target_channel_id);
  const self: TreeNode = {
    id: n.id,
    label: c.type === "thread" ? `↳ ${c.name}` : c.name,
    kind: "channel",
    disabled: !isMappable(c),
    meta: (
      <span className="sources-legend">
        {c.type !== "text" ? (
          <Badge tone="neutral" variant="outline">
            {c.type}
          </Badge>
        ) : null}
        {!c.accessible ? <Badge tone="warning">no access</Badge> : null}
        {targets.length ? (
          <Badge tone="accent" title={targets.map((t) => `#${t}`).join(", ")}>
            {"→ "}
            {targets.length === 1 ? `#${targets[0]}` : `${targets.length} targets`}
          </Badge>
        ) : null}
      </span>
    ),
  };
  return [self, ...kids(n.children)];
}

function SelectionPanel({
  guilds,
  selected,
  mappable,
  unmapped,
  mappings,
}: {
  guilds: Guild[];
  selected: ChannelNode[];
  mappable: number;
  unmapped: number;
  mappings: number;
}) {
  const totalChannels = guilds.reduce((n, g) => n + g.channel_count, 0);
  const totalMapped = guilds.reduce((n, g) => n + g.mapped_count, 0);
  return (
    <Card title="Selection">
      <dl className="kv">
        <dt>Servers</dt>
        <dd>{guilds.length}</dd>
        <dt>Channels mapped</dt>
        <dd>
          {totalMapped} of {totalChannels}
        </dd>
        <dt>Selected</dt>
        <dd>{plural(selected.length, "channel")}</dd>
        <dt>Mappable</dt>
        <dd>{mappable}</dd>
        <dt>Not yet mapped</dt>
        <dd>{unmapped}</dd>
        <dt>Existing mappings</dt>
        <dd>{mappings}</dd>
      </dl>
      <p className="faint" style={{ marginTop: "var(--space-4)" }}>
        Voice channels, categories and channels without read access cannot be mapped.
        "Create" makes a matching channel in your server per selected channel and maps it.
      </p>
    </Card>
  );
}

function MapToTargetModal({
  open,
  count,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  count: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (targetId: string) => void;
}) {
  const targets = useTargets();
  const [target, setTarget] = useState("");
  const options = [
    { value: "", label: "Choose a channel…", disabled: true },
    ...(targets.data?.items ?? []).map((t) => ({ value: t.id, label: `#${t.name}` })),
  ];
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Map ${plural(count, "channel")}`}
      description="Every selected channel will post into the chosen channel of your server."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!target}
            loading={busy}
            onClick={() => onConfirm(target)}
          >
            Map {plural(count, "channel")}
          </Button>
        </>
      }
    >
      <Select
        label="Target channel"
        value={target}
        options={options}
        onChange={(e) => setTarget(e.target.value)}
        error={targets.error ? errorMessage(targets.error) : undefined}
      />
    </Modal>
  );
}
